import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import React, { useEffect, useState } from 'react';
import Modal from '@ui/modal/Modal';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';
import useModal from '@app/component/overlays/modal/ModalRef';
import FormButton from '@ui/button/FormButton';
import _isEmpty from 'lodash/isEmpty';
import useAppForm from '@app/site/_form';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import * as z from 'zod/v4';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import { useTranslation } from '@app/component/translation/Translation';
import PricingRoomName from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName';
import { pricingRoomName } from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName.utilities';


export interface PricingArchitectureConfigurationModalProps {
	architectures: PricingArchitecture[];
	pricingRoom: PricingRoom | null;
	onClose: () => void;
}

const PricingArchitectureConfigurationModal = React.memo(
	function PricingArchitectureConfigurationModal({
																										architectures,
																										pricingRoom,
																										onClose,
																									}: PricingArchitectureConfigurationModalProps) {

		const [mode, setMode] = useState<'Anchor' | 'Derivative'>('Anchor');

		useEffect(() => {
			if (pricingRoom)
				setMode('Anchor');
		}, [pricingRoom]);


		return <>
			{mode === 'Anchor' && pricingRoom &&
				<CreatePricingArchitecture pricingRoom={pricingRoom} architectures={architectures} mode={mode}
											onSetMode={setMode} onClose={onClose} />}
			{mode === 'Derivative' && !_isEmpty(architectures) && pricingRoom &&
				<AddPricingArchitectureDerivative pricingRoom={pricingRoom} architectures={architectures}
												mode={mode} onSetMode={setMode} onClose={onClose} />}
		</>;
	});

const CreatePricingArchitecture: React.FunctionComponent<{
	pricingRoom: PricingRoom | null,
	architectures: PricingArchitecture[],
	mode: 'Anchor' | 'Derivative',
	onSetMode: (mode: 'Anchor' | 'Derivative') => void,
	onClose: () => void
}> = ({ pricingRoom, architectures, mode, onSetMode, onClose }) => {
	const modal = useModal();
	const { companyHotel } = useSelectedCompanyHotel();
	const { t } = useTranslation();

	useEffect(() => {
		if (pricingRoom && !modal.isOpen()) {
			modal.open();
		}
	}, [pricingRoom]);

	const queryClient = useQueryClient();
	const toaster = useToaster();

	const createArchitectureMutation = useMutation({
		mutationFn: PricingAutomationService.createPricingArchitecture,
		onSuccess: () => {
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				}),
				queryClient.invalidateQueries({
					queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotel.id),
				}),
			]).then(() => {
				modal.close();
				toaster.success('Pricing Architecture', 'Pricing Architecture created successfully');
			});
		},
	});

	const form = useAppForm({
		defaultValues: {
			roomType: HotelRoomMappingType.SINGLE,
		},
		validators: {
			onChange: z.object({
				roomType: z.nativeEnum(HotelRoomMappingType),
			}),
		},
		onSubmit: ({ value }) =>
			createArchitectureMutation.mutateAsync({
				companyHotelId: companyHotel.id,
				pricingRoomId: pricingRoom!.id,
				competitorsMappingType: value.roomType,
			}).catch((e) => {
				modal.close();
				toaster.error('Pricing Architecture', t(e));
			}),
	});

	const roomMappingsOptions = companyHotel.roomMappingTypeConfigurations
		.filter(m => m.isEnabled && m.type !== HotelRoomMappingType.NONE)
		.map(m => {
			return { id: m.type, label: m.name, value: m.type };
		});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<Modal ref={modal.ref} title="Configure Room" width="large" onClose={onClose} actions={
				<form.SubmitButton state="primary">
					Save
				</form.SubmitButton>}>
				{pricingRoom && <div className="mt-6">
					<div className="mb-4 text-sm text-gray-600">
						Assign <strong><PricingRoomName room={pricingRoom} /></strong> to your strategy.</div>
					<div className="flex gap-2">
						<FormButton state={mode === 'Anchor' ? 'primary' : 'white'} onClick={() => onSetMode('Anchor')}
									extraClassName="flex-col flex-1">
							<span className="font-bold tracking-wide">Make Room Anchor</span>
							<span className="text-xs font-light">Directly follow market</span>
						</FormButton>
						<FormButton disabled={_isEmpty(architectures)} state={mode === 'Derivative' ? 'primary' : 'white'}
									onClick={() => onSetMode('Derivative')} extraClassName="flex-col flex-1">
							<span className="font-bold tracking-wide">Make Room Derived</span>
							<span className="text-xs font-light">Follow's another room's price</span>
						</FormButton>
					</div>
					<div className="mt-6 space-y-4">
						<div>
							<div className="flex justify-between">
								<label htmlFor="room-type" className="label">Select Market Group</label>
							</div>
							<div className="mt-0.5">
								<form.AppField name="roomType">
									{field => <field.SelectField size="w-full" optionsSize="large" options={roomMappingsOptions} />}
								</form.AppField>
							</div>
							<p className="help text-xs">This room will track the selected market group's average rate.</p></div>
					</div>
				</div>}
			</Modal>
		</form>
	</form.AppForm>;
};


const AddPricingArchitectureDerivative: React.FunctionComponent<{
	pricingRoom: PricingRoom | null,
	architectures: PricingArchitecture[],
	mode: 'Anchor' | 'Derivative',
	onSetMode: (mode: 'Anchor' | 'Derivative') => void,
	onClose: () => void
}> = ({ pricingRoom, architectures, mode, onSetMode, onClose }) => {
	const modal = useModal();
	const { companyHotel } = useSelectedCompanyHotel();

	useEffect(() => {
		if (pricingRoom) {
			modal.open();
		}
	}, [pricingRoom]);


	const addDerivativeSchema = z.object({
		architectureId: z.number().min(1, 'Please select an architecture'),
		companyHotelId: z.number().min(1, 'Please select company hotel'),
		pricingRoomId: z.number().min(1, 'Please select a room'),
		ruleType: z.nativeEnum(PricingArchitectureDerivativeRuleType),
		ruleValue: z.string().optional(),
	}).superRefine((value, ctx) => {
		if (value.ruleType !== PricingArchitectureDerivativeRuleType.SAME) {
			const ruleValueNumber = value.ruleValue === undefined || value.ruleValue.trim() === ''
				? Number.NaN
				: Number(value.ruleValue);

			if (Number.isNaN(ruleValueNumber)) {
				ctx.addIssue({
					code: 'custom',
					path: ['ruleValue'],
					message: 'Please enter a valid value',
				});
			}
		}
	});

	const queryClient = useQueryClient();
	const toaster = useToaster();

	const createArchitectureDerivativeMutation = useMutation({
		mutationFn: PricingAutomationService.createPricingArchitectureDerivative,
		onSuccess: () => {
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id),
				}),
				queryClient.invalidateQueries({
					queryKey: PricingAutomationQueryKeys.notAssignedPricingRooms(companyHotel.id),
				}),
			]).then(() => {
				modal.close();
				onClose();
				toaster.success('Pricing Architecture', 'Pricing Architecture Derivative added successfully');
			});
		},
	});

	const defaultValues: z.input<typeof addDerivativeSchema> = {
		architectureId: architectures[0].id,
		companyHotelId: companyHotel.id,
		pricingRoomId: pricingRoom!.id,
		ruleType: PricingArchitectureDerivativeRuleType.SAME,
		ruleValue: '0',
	};

	const form = useAppForm({
		defaultValues: defaultValues,
		validators: {
			onChange: addDerivativeSchema,
		},
		onSubmit: ({ value }) =>
			createArchitectureDerivativeMutation.mutateAsync({
				architectureId: value.architectureId,
				companyHotelId: value.companyHotelId,
				pricingRoomId: value.pricingRoomId,
				ruleType: value.ruleType,
				ruleValue: value.ruleValue !== undefined && value.ruleValue !== '' ? Number(value.ruleValue) : undefined,
			}).catch(() => toaster.error('Pricing Architecture', 'Pricing Architecture Derivative creation failed')),
	});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<Modal ref={modal.ref} title="Configure Room" width="large" onClose={onClose} actions={
				<form.SubmitButton state="primary">
					Save
				</form.SubmitButton>}>
				{pricingRoom && <div className="mt-6">
					<div className="mb-4 text-sm text-gray-600">
						Assign <strong><PricingRoomName room={pricingRoom} /></strong> to your strategy.</div>
					<div className="flex gap-2">
						<FormButton state={mode === 'Anchor' ? 'primary' : 'white'} onClick={() => onSetMode('Anchor')}
									extraClassName="flex-col flex-1">
							<span className="font-bold tracking-wide">Make Room Anchor</span>
							<span className="text-xs font-light">Directly follow market</span>
						</FormButton>
						<FormButton disabled={_isEmpty(architectures)} state={mode === 'Derivative' ? 'primary' : 'white'}
									onClick={() => onSetMode('Derivative')} extraClassName="flex-col flex-1">
							<span className="font-bold tracking-wide">Make Room Derived</span>
							<span className="text-xs font-light">Follow's another room's price</span>
						</FormButton>
					</div>
					<div className="mt-6 space-y-4">
						<div>
							<div className="flex justify-between"><label htmlFor="room-type" className="label">Parent Anchor</label>
							</div>
							<div className="mt-0.5">
								<form.AppField name="architectureId">
									{field => <field.SelectField extraClassName="w-full" optionsSize="xxlarge"
																options={architectures.map(architecture => ({
																									id: '' + architecture.id,
																label: pricingRoomName(architecture.anchor) + ' · ' + architecture.competitorsMappingType,
																									value: architecture.id,
																								}))} />}
								</form.AppField>
							</div>
						</div>
						<div className="flex gap-2">
							<div className="flex-1">
								<div className="flex justify-between">
									<label htmlFor="type" className="label">Type</label>
								</div>
								<div className="mt-0.5">
									<form.AppField name="ruleType">
										{field => <field.SelectField extraClassName="w-full"
																	options={[
																										{
																											id: 'SAME',
																											label: 'Equal',
																											value: PricingArchitectureDerivativeRuleType.SAME,
																										},
																										{
																											id: 'PERCENTAGE',
																											label: 'Percentage (%)',
																											value: PricingArchitectureDerivativeRuleType.PERCENTAGE,
																										},
																										{
																											id: 'AMOUNT',
																											label: 'Amount',
																											value: PricingArchitectureDerivativeRuleType.AMOUNT,
																										},
																									]} />}
									</form.AppField>
								</div>
							</div>
							<div className="flex-1">
								<div className="flex justify-between">
									<label htmlFor="difference" className="label">Differential</label>
								</div>
								<div className="mt-0.5">
									<form.Subscribe selector={(state) => state.values.ruleType}>
										{(ruleType) => (
											<form.AppField name="ruleValue">
												{(field) => (
													<field.TextField
														extraClassName="w-full"
														disabled={ruleType === PricingArchitectureDerivativeRuleType.SAME}
													/>
												)}
											</form.AppField>
										)}
									</form.Subscribe>
								</div>
							</div>
						</div>
					</div>
				</div>}
			</Modal>
		</form>
	</form.AppForm>;
};


export default PricingArchitectureConfigurationModal;
